import { useLayoutEffect, useRef, useState } from "react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import styled from "styled-components";
import Footer from "../../components/fragments/Footer";
import NavBar from "../../components/fragments/NavBar";

import { size } from "../../components/styles/Breakpoints";
import { mapStyles } from "../../assets/other/mapStyles";
import { Container, Grid } from "../../components/styles/Layout";
import { Button } from "../../components/styles/Button";
import { TwitterTimelineEmbed } from "react-twitter-embed";
import Loader from "react-loader-spinner";
import sendEnquiry from "../../data/sendEnquiry";
import { FormError, HoneypotField, TurnstileWidget, turnstileEnabled } from "../../components/fragments/EnquiryFormParts";

import { useStaticSeo } from "../../hooks/useSeo";
import { STATIC_SEO } from "../../data/seo";
import useJsonLd from "../../hooks/useJsonLd";
import { organisationSchema, HOURS } from "../../data/structuredData";
import useGoogleMaps from "../../hooks/useGoogleMaps";
import { CONTACT_LINKS } from "../../data/contactLinks";

const PanelWrap = styled.div`
    display: grid;
    grid-template-columns: 2fr 1fr;
    margin-bottom: 3rem;
    box-sizing: inherit;
    @media screen and (max-width: ${size.sm}px) {
        display: block;
    }
`;

const BigTitle = styled.h2`
    font-weight: 300;
    color: #14315b;
`;
const Text = styled.p`
    max-width: 40rem;
`;

/* Contact details (and their icons and link targets) live in data/contactLinks.js. */
const ContactLink = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    color: inherit;
    transition: color 0.3s;
    &:hover,
    &:focus-visible {
        color: ${(props) => props.theme.darkblue};
        text-decoration: underline;
    }
    & svg {
        flex: none;
        width: 1.2rem;
        height: 1.2rem;
        color: ${(props) => props.theme.darkblue};
    }
`;

const HoursTitle = styled.h2`
    font-size: 1rem;
    font-weight: 500;
    margin: 1.5rem 0 0.4rem;
`;

const Hours = styled.dl`
    max-width: 18rem;
    margin: 0;
`;

const HoursRow = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 1.5rem;
    padding: 0.15rem 0;
`;

const HoursDay = styled.dt`
    margin: 0;
`;

const HoursValue = styled.dd`
    margin: 0;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
`;
const AddressPanel = styled.div`
    height: 100%;
`;
const MapPanel = styled.div`
    padding-top: 2rem;
    padding-left: 2rem;
    margin-bottom: 2rem;
`;
const ContactForm = styled.form`
    margin: 2rem 0;
`;
const InputWrap = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 1rem;
`;
const Input = styled.input`
    display: block;
    width: 100%;
    border-radius: 0;
    border: 1px solid #e7e7e7;
    padding: 0.5rem;
    font-size: 1rem;
    font-weight: 300;
    margin-bottom: 0.5rem;
`;
const Textarea = styled.textarea`
    display: block;
    width: 100%;
    border-radius: 0;
    border: 1px solid #e7e7e7;
    padding: 0.5rem;
    font-size: 1rem;
    font-weight: 300;
    margin-bottom: 0.5rem;
    min-height: 10rem;
    resize: vertical;
`;

const SendLoader = styled(Loader)`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const SendButton = styled(Button)`
    pointer-events: ${(props) => (props.sending ? "none" : "auto")};
    background: ${(props) => props.sent && props.theme.grey};
`;

// MAP Options & functions
const options = {
    styles: mapStyles,
    disableDefaultUI: true,
    zoomControl: true,
    scrollwheel: false,
};

const mapContainerStyle = {
    width: "100%",
    height: "30rem",
};

export default function Contact() {
    useStaticSeo(STATIC_SEO["/contact"]);
    useJsonLd(organisationSchema());

    // Maps loads on demand now - render nothing until it is ready,
    // because the marker icons below build window.google objects.
    const mapsReady = useGoogleMaps();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [buttonText, setButtonText] = useState("Send");
    const [isSending, setIsSending] = useState(false);
    const [messageSent, setMessageSent] = useState(false);
    const [error, setError] = useState("");
    const [trap, setTrap] = useState("");
    const [captchaToken, setCaptchaToken] = useState("");
    const [captchaReset, setCaptchaReset] = useState(0);

    useLayoutEffect(() => {
        document.getElementById("root").scrollTo(0, 0);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSending || messageSent) return;
        if (turnstileEnabled && !captchaToken) {
            setError("Please complete the security check below, then press Send.");
            return;
        }

        setError("");
        setIsSending(true);
        try {
            await sendEnquiry({ type: "contact", name, email, message, fax: trap, turnstileToken: captchaToken });
            setMessageSent(true);
            setButtonText("Message Sent");
        } catch (err) {
            setError(err.message);
            setCaptchaReset((n) => n + 1); // a Turnstile token works once
        }
        setIsSending(false);
    };

    return (
        <>
            <NavBar location="public" pageTitle="Contact" />
            <Container>
                <PanelWrap>
                    <AddressPanel>
                        <BigTitle>Property People Ltd</BigTitle>
                        {CONTACT_LINKS.map(({ icon: Icon, text, href, label, external }) => (
                            <Text key={href}>
                                <ContactLink
                                    href={href}
                                    aria-label={label}
                                    {...(external && { target: "_blank", rel: "noopener noreferrer" })}
                                >
                                    <Icon aria-hidden="true" />
                                    {text}
                                </ContactLink>
                            </Text>
                        ))}
                        <HoursTitle>Opening hours</HoursTitle>
                        <Hours>
                            {HOURS.map((h) => (
                                <HoursRow key={h.label}>
                                    <HoursDay>{h.label}</HoursDay>
                                    <HoursValue>{h.display}</HoursValue>
                                </HoursRow>
                            ))}
                        </Hours>
                        <ContactForm onSubmit={handleSubmit}>
                            <HoneypotField value={trap} onChange={setTrap} />
                            <InputWrap>
                                <Input
                                    type="text"
                                    placeholder="Name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                />
                                <Input
                                    type="email"
                                    placeholder="Email address"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                            </InputWrap>
                            <Textarea
                                placeholder="Message"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                            ></Textarea>
                            <TurnstileWidget onToken={setCaptchaToken} resetKey={captchaReset} />
                            <SendButton
                                primary
                                sending={isSending || messageSent}
                                sent={messageSent}
                            >
                                {isSending ? (
                                    <SendLoader type="Puff" color="white" height={19} width={19} />
                                ) : (
                                    <>{buttonText}</>
                                )}
                            </SendButton>
                            {error && <FormError role="alert">{error}</FormError>}
                        </ContactForm>
                        {mapsReady && (
                            <GoogleMap
                                center={{ lat: 51.4235821, lng: -0.1751521 }}
                                mapContainerStyle={mapContainerStyle}
                                zoom={12}
                                options={options}
                            >
                                <Marker
                                    position={{
                                        lat: 51.4235821,
                                        lng: -0.1751521,
                                    }}
                                    icon={{
                                        url: "/images/pp-pin-letting.svg",
                                        scaledSize: new window.google.maps.Size(40, 40),
                                    }}
                                ></Marker>
                            </GoogleMap>
                        )}
                    </AddressPanel>
                    <MapPanel>
                        <TwitterTimelineEmbed
                            sourceType="profile"
                            screenName="Property_People"
                            options={{ height: 1100 }}
                        />
                    </MapPanel>
                </PanelWrap>
            </Container>
            <Footer />
        </>
    );
}
